import gql from 'graphql-tag'

import { IMAGE_FIELDS_FRAG, PACKAGE_ADDONS_FIELDS } from './general'

export const BASIC_MY_BOOKINGS_FIELDS = gql`
  fragment BasicMyBookingFields on Booking {
    id
    status
    eventComponent {
      id
      customTexts
      offering {
        __typename
      }
    }
  }
`

export const OFFERING_ADDONS_FIELDS = gql`
  fragment OfferingAddonsFields on OfferingAddOns {
    id
    frequency
    label
    price
    mandatory
    quantity
    metadata
    type
    offeringAddOnOptions {
      id
      description
      label
      price
      quantity
      offeringAddOnId
    }
  }
`

// EC = Event Component
export const RSVP_EC = gql`
  fragment EventComponentFields on EventComponent {
    id
    date
    duration
    alias
    status
    capacity
    eventId
    customTexts
    details
    order
    booking {
      id
      status
      guest {
        id
      }
    }
    main
    packageCustomization {
      ...PackageAddonsFields
    }
    tiers {
      id
      name
    }
    bookingCount
    guestPays
    offering {
      __typename
      ... on PackageOffering {
        name
        intro
        description
        mainImage {
          ...ImageFields
        }
        images {
          ...ImageFields
        }
        addOns {
          ...OfferingAddonsFields
        }
        provider {
          timeToAirport
          airportCode
          coord
          id
          url
          address
          location
          name
          mainImage {
            ...ImageFields
          }
          images {
            ...ImageFields
          }
        }
      }
      ... on RoomOffering {
        name
        description
        mainImage {
          ...ImageFields
        }
        images {
          ...ImageFields
        }
        addOns {
          ...OfferingAddonsFields
        }
        price
        room {
          beds
          occupancy
          size
          basic
        }
        provider {
          timeToAirport
          airportCode
          coord
          id
          url
          address
          location
          name
          mainImage {
            ...ImageFields
          }
          images {
            ...ImageFields
          }
        }
      }
    }
  }
  ${IMAGE_FIELDS_FRAG}
  ${PACKAGE_ADDONS_FIELDS}
  ${OFFERING_ADDONS_FIELDS}
`

export const GUEST = gql`
  query guestMe {
    me {
      ... on Guest {
        event {
          id
          name
          description
          video
          brideFirstName
          brideLastName
          groomFirstName
          groomLastName
          invitationTemplate {
            template
          }
          host {
            firstName
            lastName
          }
        }
        status
        id
        email
        firstName
        lastName
        kidsAllowed
        plusOne
        bookedComponents
        eventComponents {
          ...EventComponentFields
        }
        mainComponent {
          ...EventComponentFields
        }
      }
    }
  }
  ${RSVP_EC}
`

export const FLIGHT_DATA = gql`
  fragment flightData on FlightInformation {
    adults
    arrivalAirline
    arrivalAirport
    arrivalDatetime
    arrivalFlight
    arrivalLocation
    departureAirline
    departureAirport
    departureDatetime
    departureFlight
    departureLocation
    guestNames
    id
    kids
    propertyName
  }
`

export const BOOKING_ADDONS_FIELDS = gql`
  fragment BookingAddonsFields on BookingAddOn {
    id
    offeringAddOnOption {
      id
      label
      price
      description
      quantity
      offeringAddOnId
    }
    offeringAddOn {
      id
      frequency
      label
      mandatory
      taxable
      format
      price
      type
      metadata
    }
    basicInfo
    guest {
      ... on GuestPublic {
        id
        firstName
        lastName
      }
      ... on GuestShort {
        id
        firstName
        lastName
        phone
        email
        isPlusOne
        eventId
        flightInformation {
          ...flightData
        }
      }
    }
  }
  ${FLIGHT_DATA}
`

export const PROVIDER_FIELDS_FOR_BOOKING = gql`
  fragment ProviderFields on Provider {
    bookAllRequired
    airportCode
    location
    id
    address
    url
    name
    accomodationTaxRate
    venueTaxRate
    cancellationPolicy
  }
`

export const SEND_INVITATION = gql`
  mutation sendInvitation($id: ID!) {
    sendInvitation(id: $id) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const SEND_REMINDER = gql`
  mutation sendReminder($guestId: ID!, $template: String!) {
    sendReminder(guestId: $guestId, template: $template) {
      result {
        id
        status
        reminderSentAt
      }
      errors {
        key
        message
      }
    }
  }
`

export const GUEST_EVENT = gql`
  mutation triggerGuestEvent($guestId: ID!, $event: GuestEvent!) {
    triggerGuestEvent(guestId: $guestId, event: $event) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const UPDATE_MY_INVITATION = gql`
  mutation updateMyInvitation($guestId: ID!, $event: GuestEvent!) {
    updateMyInvitation(guestId: $guestId, event: $event) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const RSVP_PREVIEW_DATA = gql`
  query getMyEventInfoRSVP($id: ID!) {
    me {
      id
      ... on Customer {
        myEventInfo(id: $id) {
          ...commonEventFieldsRSVP
        }
      }
      ... on Concierge {
        myEventInfo(id: $id) {
          ...commonEventFieldsRSVP
        }
      }
      ... on Admin {
        myEventInfo(id: $id) {
          ...commonEventFieldsRSVP
        }
      }
    }
  }
  fragment commonEventFieldsRSVP on MyEvent {
    defaultKidsAllowed
    defaultPlusOne
    id
    name
    description
    components {
      ...EventComponentFields
    }
    video
    brideFirstName
    brideLastName
    groomFirstName
    groomLastName
    invitationTemplate {
      template
    }
  }
  ${RSVP_EC}
`

export const CREATE_BOOKING = gql`
  mutation createBooking($eventComponentId: ID!) {
    createBooking(eventComponentId: $eventComponentId) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const CREATE_BOOKING_FOR_GUEST = gql`
  mutation createBookingForGuest($eventComponentId: ID!, $guestId: ID!) {
    createBookingForGuest(
      eventComponentId: $eventComponentId
      guestId: $guestId
    ) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const CHECK_BOOKING_AVAILABILITY = gql`
  query checkBookingAvailability($bookingId: ID!, $userId: ID!) {
    booking(id: $bookingId, userId: $userId) {
      id
      availability
      eventComponent {
        id
        capacity
        bookingCount
      }
    }
  }
`

const GUEST_GROUP_FIELDS = gql`
  fragment GuestGroupFields on GuestShort {
    id
    email
    firstName
    lastName
    phone
    group {
      primaryContact
    }
  }
`
export const GET_GUEST_GROUP = gql`
  query getMyGroup($eventId: ID!) {
    me {
      ... on Customer {
        myGroup(eventId: $eventId) {
          ...GuestGroupFields
        }
      }
      ... on Concierge {
        myGroup(eventId: $eventId) {
          ...GuestGroupFields
        }
      }
      ... on Admin {
        myGroup(eventId: $eventId) {
          ...GuestGroupFields
        }
      }
    }
  }
  ${GUEST_GROUP_FIELDS}
`

export const SEARCH_GUESTS_IN_BOOKING = gql`
  query searchGuestsInBooking($bookingId: ID!, $userId: ID!, $search: String!) {
    booking(id: $bookingId, userId: $userId) {
      id
      eventGuests(search: $search) {
        id
        firstName
        lastName
      }
    }
  }
`

export const GET_BOOKING_DATA = gql`
  query getBookingInfo($id: ID!, $userId: ID!) {
    me {
      ... on Customer {
        eventList {
          id
          name
        }
        myEventList {
          id
          name
        }
      }
      ... on Concierge {
        eventList {
          id
          name
        }
        myEventList {
          id
          name
        }
      }
      ... on Admin {
        eventList {
          id
          name
        }
        myEventList {
          id
          name
        }
      }
    }
    booking(id: $id, userId: $userId) {
      id
      status
      availability
      date
      duration
      insertedAt
      updatedAt
      guest {
        id
        plusOne
        plusOneCount
        firstName
        lastName
        email
        phone
        flightInformation {
          ...flightData
        }
        group {
          primaryContact
        }
      }
      guestPrice {
        basePrice
        tax
        total
        offeringPrice
        duration
      }
      bookingAddOn {
        ...BookingAddonsFields
      }
      eventComponent {
        id
        date
        duration
        capacity
        guestPays
        eventId
        alias
        main
        deadline
        softDeadline
        items {
          deleted
          master
          price
        }
        event {
          id
          name
          guestProfile {
            id
          }
        }
        details
        customTexts
        packageCustomization {
          ...PackageAddonsFields
        }
        bookingCount
        offering {
          __typename
          ... on PackageOffering {
            name
            intro
            description
            mainImage {
              ...ImageFields
            }
            images {
              ...ImageFields
            }
            provider {
              ...ProviderFields
            }
            price
            addOns {
              ...OfferingAddonsFields
            }
            package {
              basic
            }
          }
          ... on RoomOffering {
            name
            id
            description
            mainImage {
              ...ImageFields
            }
            images {
              ...ImageFields
            }
            provider {
              ...ProviderFields
            }
            price
            room {
              beds
              occupancy
              size
              basic
            }
            addOns {
              ...OfferingAddonsFields
            }
          }
        }
      }
    }
  }
  ${IMAGE_FIELDS_FRAG}
  ${PACKAGE_ADDONS_FIELDS}
  ${FLIGHT_DATA}
  ${OFFERING_ADDONS_FIELDS}
  ${BOOKING_ADDONS_FIELDS}
  ${PROVIDER_FIELDS_FOR_BOOKING}
`

export const VALIDATE_GUEST_PAYMENT = gql`
  mutation validateGuestPayment($paymentIntent: String!) {
    validateGuestPayment(paymentIntent: $paymentIntent) {
      result {
        paymentStatus
        booking {
          id
          status
        }
      }
      errors {
        key
        message
      }
    }
  }
`

export const INITIATE_GUEST_PAYMENT = gql`
  mutation initiateGuestPayment($bookingId: ID!) {
    initiateGuestPayment(bookingId: $bookingId) {
      result {
        id
        clientSecret
        amount
      }
      errors {
        key
        message
      }
    }
  }
`

export const CONFIRM_BOOKING = gql`
  mutation confirmBooking(
    $bookingId: ID!
    $extraNights: ExtraNightsAddon
    $freeGuests: [GuestBookingAddon]
    $paidGuests: [GuestBookingAddon]
    $addonsToDelete: [ID!]
  ) {
    confirmBooking(
      bookingId: $bookingId
      freeGuests: $freeGuests
      paidGuests: $paidGuests
      extraNights: $extraNights
      addonsToDelete: $addonsToDelete
    ) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const EDIT_BOOKING = gql`
  mutation editBooking(
    $bookingId: ID!
    $freeGuests: [GuestBookingAddon]
    $addonsToDelete: [ID!]
  ) {
    editBooking(
      bookingId: $bookingId
      freeGuests: $freeGuests
      addonsToDelete: $addonsToDelete
    ) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const DECLINE_BOOKING = gql`
  mutation declineBooking(
    $id: ID!
    $freeGuests: [GuestBookingAddon]
    $addonsToDelete: [ID!]
  ) {
    declineBooking(
      id: $id
      freeGuests: $freeGuests
      addonsToDelete: $addonsToDelete
    ) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const CANCEL_BOOKING = gql`
  mutation cancelBooking($id: ID!) {
    cancelBooking(id: $id) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const GET_INVITATION_DATA = gql`
  query getInviatationData($eventId: ID!) {
    me {
      id
      ... on Customer {
        myBookings(eventId: $eventId) {
          ...BasicMyBookingFields
        }
        myEventList {
          id
        }
        invitations {
          ...commonInvitationsFields
        }
        invitation(eventId: $eventId) {
          ...commonInvitationFields
        }
      }
      ... on Concierge {
        myBookings(eventId: $eventId) {
          ...BasicMyBookingFields
        }
        myEventList {
          id
        }
        invitations {
          ...commonInvitationsFields
        }
        invitation(eventId: $eventId) {
          ...commonInvitationFields
        }
      }
      ... on Admin {
        myBookings(eventId: $eventId) {
          ...BasicMyBookingFields
        }
        myEventList {
          id
        }
        invitations {
          ...commonInvitationsFields
        }
        invitation(eventId: $eventId) {
          ...commonInvitationFields
        }
      }
    }
  }
  fragment commonInvitationsFields on Invitation {
    eventId
    guestId
    status
    kidsAllowed
    plusOne
  }
  fragment commonInvitationFields on InvitationInfo {
    id
    name
    description
    video
    brideFirstName
    brideLastName
    groomFirstName
    groomLastName
    invitationTemplate {
      template
    }
    host {
      firstName
      lastName
    }
    components {
      ...EventComponentFields
      event {
        guestProfile {
          id
        }
      }
    }
  }
  ${RSVP_EC}
  ${BASIC_MY_BOOKINGS_FIELDS}
`

export const GET_BOOKINGS_BY_IDS = gql`
  query getBookingsByIds($ids: [ID!]!) {
    bookings(ids: $ids) {
      id
      insertedAt
      updatedAt
      status
      date
      duration
      guest {
        id
        firstName
        lastName
      }
      guestPrice {
        total
      }
      bookingAddOn {
        id
        guest {
          ... on GuestShort {
            email
            id
            firstName
            lastName
            phone
            isPlusOne
          }
        }
        offeringAddOn {
          metadata
        }
        offeringAddOnOption {
          quantity
        }
      }
      eventComponent {
        id
        date
        alias
        duration
        guestPays
        __typename
        offering {
          ... on RoomOffering {
            id
            name
            mainImage {
              ...ImageFields
            }
            provider {
              name
            }
          }
          ... on PackageOffering {
            id
            name
            mainImage {
              ...ImageFields
            }
            provider {
              name
            }
          }
        }
      }
    }
  }
  ${IMAGE_FIELDS_FRAG}
`

export const ADD_ALL_ADDONS_TO_BOOKING = gql`
  mutation addAllAddonsToBooking(
    $bookingId: ID!
    $extraNights: ExtraNightsAddon
    $freeGuests: [GuestBookingAddon]
    $paidGuests: [GuestBookingAddon]
    $addonsToDelete: [ID!]
  ) {
    addAllAddonsToBooking(
      bookingId: $bookingId
      freeGuests: $freeGuests
      paidGuests: $paidGuests
      extraNights: $extraNights
      addonsToDelete: $addonsToDelete
    ) {
      result {
        id
      }
      errors {
        key
        message
      }
    }
  }
`

export const ADD_FLIGHT_INFORMATION = gql`
  mutation addFlightInformation(
    $guestId: ID!
    $input: FlightInformationInput!
  ) {
    addFlightInformation(guestId: $guestId, input: $input) {
      result {
        ...flightData
      }
      errors {
        key
        message
      }
    }
  }
  ${FLIGHT_DATA}
`

export const UPDATE_FLIGHT_INFORMATION = gql`
  mutation updateFlightInformation(
    $guestId: ID!
    $input: FlightInformationInput!
  ) {
    updateFlightInformation(guestId: $guestId, input: $input) {
      result {
        ...flightData
      }
      errors {
        key
        message
      }
    }
  }
  ${FLIGHT_DATA}
`

export const MANAGE_GUEST_BOOKING = gql`
  mutation manageGuestBooking(
    $id: ID!
    $eventId: ID!
    $action: HostBookingAction!
  ) {
    manageGuestBooking(id: $id, eventId: $eventId, action: $action) {
      result {
        id
        status
      }
      errors {
        key
        message
      }
    }
  }
`

export const CAN_MOVE_GUESTS_TO_GROUP = gql`
  query canMoveGuestsToGroup($guests: [ID!]!) {
    canMoveGuestsToGroup(guests: $guests) {
      guests
    }
  }
`
